import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { AuditTrailItem, AuditTrailShiftGateway } from '@wilson/api/gateway';
import {
  AuditTrailDrawerState,
  AuditTrailShiftState,
  ResetAuditTrailDrawerState,
  SetAuditTrailDrawerOpenState,
  UpsertAuditTrailShiftState,
} from '@wilson/audit-trail/states';
import {
  CustomNzDrawerService,
  NzDrawerId,
} from '@wilson/non-domain-specific/ng-zorro/nz-drawer/core';
import { ShiftsService } from '@wilson/shifts';
import {
  catchError,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
} from 'rxjs';

const AUDIT_TRAIL_TYPES_REQUIRING_ASSIGNED_USER = ['ongoing', 'completed'];

@Component({
  selector: 'wilson-audit-trail-drawer',
  templateUrl: './audit-trail-drawer.component.html',
  styleUrls: ['./audit-trail-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AuditTrailDrawerComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly customNzDrawerService = inject(CustomNzDrawerService);
  private readonly auditTrailShiftGateway = inject(AuditTrailShiftGateway);
  private readonly shiftsService = inject(ShiftsService);
  private readonly cdRef = inject(ChangeDetectorRef);

  protected drawerId = NzDrawerId.ShiftChangeHistoryDrawer;

  @Select(AuditTrailDrawerState.isOpen)
  drawerIsOpen$!: Observable<boolean>;
  drawerIsOpenSubscription$!: Subscription;
  drawerDataFetchSubscription$!: Subscription;
  auditTrail$!: Observable<AuditTrailItem[]>;
  protected currentShiftAssignedUserId?: string;

  ngOnInit(): void {
    this.store.dispatch(new ResetAuditTrailDrawerState());
    this.cdRef.detectChanges();

    this.drawerIsOpenSubscription$ = this.drawerIsOpen$.subscribe(
      (drawerIsOpen) => {
        if (drawerIsOpen) {
          this.customNzDrawerService.hideMask();
        } else {
          this.customNzDrawerService.showMask();
        }
      },
    );
    this.drawerDataFetchSubscription$ = combineLatest([
      this.store.select(AuditTrailDrawerState.currentShiftId),
      this.drawerIsOpen$,
    ])
      .pipe(
        filter(
          ([currentShiftId, drawerIsOpen]) => !!currentShiftId && drawerIsOpen,
        ),
        switchMap(([currentShiftId]) =>
          this.auditTrailShiftGateway.getAuditTrailShift(currentShiftId).pipe(
            take(1),
            switchMap((shiftAudit) =>
              this.fetchAssignedUserIdIfNeeded(shiftAudit, currentShiftId).pipe(
                map((assignedUserId) => ({
                  shiftAudit,
                  currentShiftId,
                  assignedUserId,
                })),
              ),
            ),
          ),
        ),
      )
      .subscribe(({ shiftAudit, currentShiftId, assignedUserId }) => {
        this.store.dispatch(
          new UpsertAuditTrailShiftState(currentShiftId, shiftAudit),
        );
        this.auditTrail$ = this.store.select(
          AuditTrailShiftState.auditById(currentShiftId),
        );
        this.currentShiftAssignedUserId = assignedUserId;
        this.cdRef.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.drawerIsOpenSubscription$.unsubscribe();
    this.drawerDataFetchSubscription$.unsubscribe();
    this.customNzDrawerService.showMask();
    this.store.dispatch(new ResetAuditTrailDrawerState());
  }

  closeDrawer(): void {
    this.store.dispatch(new SetAuditTrailDrawerOpenState(false));
  }

  private fetchAssignedUserIdIfNeeded(
    shiftAudit: AuditTrailItem[],
    shiftId: string,
  ): Observable<string | undefined> {
    const needsAssignedUser = shiftAudit.some((auditTrailItem) =>
      auditTrailItem.types.some((type) =>
        AUDIT_TRAIL_TYPES_REQUIRING_ASSIGNED_USER.includes(type),
      ),
    );
    if (!needsAssignedUser) {
      return of(undefined);
    }
    return this.shiftsService.getShiftUser(shiftId).pipe(
      map((user) => user?.id),
      // This observable is nested inside the audit-trail switchMap chain,
      // so an unhandled error here would terminate the whole subscription
      // and silently break the drawer until the component is recreated.
      // Fall back to "no assigned user" instead of letting that happen.
      catchError(() => of(undefined)),
    );
  }
}
